/**
 * @fileoverview A component that displays a set of buttons for selecting social media platforms.
 */
import React from 'react';
import { Platform } from '../types';
import { FacebookIcon, InstagramIcon, XIcon, YouTubeIcon, MastodonIcon, ThreadsIcon, BlueskyIcon, TikTokIcon } from './icons';
import { Tooltip } from './Tooltip';

interface PlatformSelectorProps {
  selectedPlatforms: Platform[]; // An array of currently selected platforms.
  onSelectionChange: (platforms: Platform[]) => void; // Callback to update the parent component's state.
  disabledPlatforms?: Platform[];
  disabledReasons?: Partial<Record<Platform, string>>;
}

// Configuration array for platform buttons, defining the icon and styling for each.
const platformOptions = [
  { 
    id: Platform.Facebook, 
    Icon: FacebookIcon,
    selectedClasses: 'bg-[#1877F2] border-[#1877F2] text-white',
    hoverClasses: 'hover:bg-[#1877F2] hover:border-[#1877F2] hover:text-white dark:hover:bg-[#1877F2] dark:hover:border-[#1877F2]' 
  },
  { 
    id: Platform.Instagram, 
    Icon: InstagramIcon,
    selectedClasses: 'bg-gradient-to-br from-[#833ab4] via-[#fd1d1d] to-[#fcb045] border-transparent text-white',
    hoverClasses: 'hover:bg-gradient-to-br hover:from-[#833ab4] hover:via-[#fd1d1d] hover:to-[#fcb045] hover:border-transparent hover:text-white' 
  },
  { 
    id: Platform.X, 
    Icon: XIcon,
    selectedClasses: 'bg-black border-black text-white dark:bg-white dark:border-white dark:text-black',
    hoverClasses: 'hover:bg-black dark:hover:bg-white hover:text-white dark:hover:text-black border-slate-300 dark:border-gray-600 hover:border-black dark:hover:border-white' 
  },
  { 
    id: Platform.YouTube, 
    Icon: YouTubeIcon,
    selectedClasses: 'bg-[#FF0000] border-[#FF0000] text-white',
    hoverClasses: 'hover:bg-[#FF0000] hover:border-[#FF0000] hover:text-white dark:hover:bg-[#FF0000] dark:hover:border-[#FF0000]'
  },
  {
    id: Platform.Mastodon,
    Icon: MastodonIcon,
    selectedClasses: 'bg-[#6364FF] border-[#6364FF] text-white',
    hoverClasses: 'hover:bg-[#6364FF] hover:border-[#6364FF] hover:text-white dark:hover:bg-[#6364FF] dark:hover:border-[#6364FF]'
  },
  {
    id: Platform.Threads,
    Icon: ThreadsIcon,
    selectedClasses: 'bg-black border-black text-white dark:bg-white dark:border-white dark:text-black',
    hoverClasses: 'hover:bg-black dark:hover:bg-white hover:text-white dark:hover:text-black border-slate-300 dark:border-gray-600 hover:border-black dark:hover:border-white'
  },
  {
    id: Platform.Bluesky,
    Icon: BlueskyIcon,
    selectedClasses: 'bg-[#007AFF] border-[#007AFF] text-white',
    hoverClasses: 'hover:bg-[#007AFF] hover:border-[#007AFF] hover:text-white dark:hover:bg-[#007AFF] dark:hover:border-[#007AFF]'
  },
  {
    id: Platform.TikTok,
    Icon: TikTokIcon,
    selectedClasses: 'bg-black border-black text-white dark:bg-white dark:border-white dark:text-black',
    hoverClasses: 'hover:bg-black dark:hover:bg-white hover:text-white dark:hover:text-black border-slate-300 dark:border-gray-600 hover:border-black dark:hover:border-white'
  },
];

export const PlatformSelector: React.FC<PlatformSelectorProps> = ({ selectedPlatforms, onSelectionChange, disabledPlatforms = [], disabledReasons }) => {
  const disabledSet = new Set(disabledPlatforms);
  /**
   * Toggles the selection status of a platform.
   * @param platform - The platform to toggle.
   */
  const handleTogglePlatform = (platform: Platform) => {
    if (disabledSet.has(platform)) {
      return;
    }
    const newSelection = selectedPlatforms.includes(platform)
      ? selectedPlatforms.filter(p => p !== platform) // Remove if already selected
      : [...selectedPlatforms, platform]; // Add if not selected
    onSelectionChange(newSelection);
  };

  return (
    <div className="flex flex-wrap gap-3">
      {platformOptions.map(({ id, Icon, selectedClasses, hoverClasses }) => {
        const isSelected = selectedPlatforms.includes(id);
        const isDisabled = disabledSet.has(id);
        const tooltipText = id;
        return (
          <Tooltip key={id} text={tooltipText} position="bottom">
            <button
              type="button"
              onClick={() => handleTogglePlatform(id)}
              className={`
                flex items-center justify-center p-3 rounded-full border-2 transition-all duration-200 ease-in-out
                transform hover:scale-110
                ${isSelected
                  ? selectedClasses // Apply selected styles
                  : `bg-slate-100 dark:bg-gray-700 border-slate-300 dark:border-gray-600 text-slate-500 dark:text-gray-400 ${hoverClasses}` // Apply default styles
                }
                ${isDisabled ? 'opacity-50 cursor-not-allowed hover:scale-100' : ''}
              `}
              aria-pressed={isSelected}
              aria-label={
                isDisabled
                  ? `${id} (disabled${disabledReasons?.[id] ? `: ${disabledReasons?.[id]}` : ''})`
                  : `Select ${id}`
              }
              disabled={isDisabled}
            >
              <Icon className="w-6 h-6" />
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
};
